"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Cpu, LockKeyhole, RefreshCw, Workflow } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

type RootHardware = {
  root_key?: string;
  provider_family?: string;
  project_ref?: string;
  project_name?: string;
  region?: string | null;
  status?: string;
  health_state?: string;
  permanent_anchor?: boolean;
  universal_replaceable?: boolean;
  ordinary_provider_switch_allowed?: boolean;
  migration_class?: string;
  architecture_version?: string;
  last_verified_at?: string | null;
};

type UniversalSubsystem = {
  subsystem_key: string;
  display_name: string;
  domain: string;
  universal: boolean;
  replaceable: boolean;
  root_exception: boolean;
  current_binding_reference?: string | null;
  active: boolean;
};

type EnterpriseFabricPayload = {
  architecture?: {
    version?: string;
    status?: string;
    canonical_commit?: string | null;
  };
  root_hardware?: RootHardware;
  universal_subsystems?: UniversalSubsystem[];
  universal_automation_targets?: {
    total?: number;
    enabled?: number;
    registered?: number;
    disabled?: number;
  };
  current_automation_hardware?: {
    binding_key?: string;
    runtime_provider?: string;
    state?: string;
    health_state?: string;
    configuration_reference?: string | null;
  };
  governance_path?: string;
  private_bypass_allowed?: boolean;
};

function tone(value?: string | null) {
  const state = String(value ?? "").toUpperCase();
  if (["ACTIVE","HEALTHY","PASS","CANONICAL"].includes(state)) {
    return "bg-emerald-400/10 text-emerald-500 dark:text-emerald-300";
  }
  if (["DEGRADED","WARN"].includes(state)) {
    return "bg-amber-400/10 text-amber-600 dark:text-amber-200";
  }
  if (["FAIL","FAILED","OUTAGE"].includes(state)) {
    return "bg-red-400/10 text-red-500 dark:text-red-300";
  }
  return "bg-foreground/[0.06] text-foreground/60";
}

export function EnterpriseFabricPanel({
  supabase,
  isOwner,
}: {
  supabase: SupabaseClient | null;
  isOwner: boolean;
}) {
  const [fabric, setFabric] = useState<EnterpriseFabricPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!supabase || !isOwner) return;
    setLoading(true);
    setNotice(null);
    try {
      const { data, error } = await supabase.functions.invoke("runyi-enterprise-fabric", {
        body: {},
      });
      if (error) throw error;
      setFabric((data ?? null) as EnterpriseFabricPayload | null);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not load Enterprise Fabric.");
    } finally {
      setLoading(false);
    }
  }, [isOwner, supabase]);

  useEffect(() => {
    if (!isOwner) return;
    const timer = window.setTimeout(() => void refresh(), 120);
    return () => window.clearTimeout(timer);
  }, [isOwner, refresh]);

  const root = fabric?.root_hardware ?? {};
  const subsystems = useMemo(
    () => (fabric?.universal_subsystems ?? []).filter((item) => !item.root_exception),
    [fabric],
  );
  const automation = fabric?.current_automation_hardware ?? {};
  const targets = fabric?.universal_automation_targets ?? {};

  if (!isOwner) return null;

  return (
    <div className="rounded-2xl bg-foreground/[0.022] p-3">
      <div className="flex items-start gap-2.5">
        <div className="grid size-6 shrink-0 place-items-center rounded-md bg-primary/[0.08] text-primary">
          <Cpu className="size-3" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold">Universal Enterprise Fabric</p>
          <p className="mt-1 text-[11px] leading-4 text-foreground/42">
            One permanent Owner root hardware anchor. Every non-root subsystem is universal and replaceable through governed bindings.
          </p>
        </div>
        <Button variant="ghost" size="icon" className="size-8" onClick={() => void refresh()} disabled={loading}>
          <RefreshCw className={"size-3.5 " + (loading ? "animate-spin" : "")} />
        </Button>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <div className="rounded-xl bg-background/55 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <LockKeyhole className="size-3.5 text-primary" />
              <p className="text-[11px] font-semibold">Owner Supabase Root Hardware</p>
            </div>
            <Badge className={tone(root.health_state)}>{root.health_state ?? "UNKNOWN"}</Badge>
          </div>
          <p className="mt-2 font-mono text-[10px] text-foreground/55">{root.project_ref ?? "—"}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge className="bg-foreground/[0.06] text-foreground/60">PERMANENT ROOT</Badge>
            <Badge className="bg-foreground/[0.06] text-foreground/60">NOT PROVIDER-SWAPPABLE</Badge>
            <Badge className="bg-foreground/[0.06] text-foreground/60">M4 GOVERNED</Badge>
          </div>
          <p className="mt-2 text-[10px] leading-4 text-foreground/40">
            Root migration class: {root.migration_class ?? "ARCHITECTURE_LEVEL_ROOT_MIGRATION_ONLY"}
          </p>
        </div>

        <div className="rounded-xl bg-background/55 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Workflow className="size-3.5 text-primary" />
              <p className="text-[11px] font-semibold">Universal Automation Hardware</p>
            </div>
            <Badge className={tone(automation.health_state)}>{automation.health_state ?? "UNKNOWN"}</Badge>
          </div>
          <p className="mt-2 font-mono text-[10px] text-foreground/55">
            {automation.binding_key ?? "MEMBER1_AUTOMATION"}
          </p>
          <p className="mt-1 text-[10px] text-foreground/40">
            {automation.configuration_reference ?? "Current replaceable runtime binding"}
          </p>
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            <div className="rounded-lg bg-foreground/[0.025] px-2 py-1.5">
              <p className="text-[9px] text-foreground/35">Targets</p>
              <p className="text-xs font-semibold">{targets.total ?? 0}</p>
            </div>
            <div className="rounded-lg bg-foreground/[0.025] px-2 py-1.5">
              <p className="text-[9px] text-foreground/35">Enabled</p>
              <p className="text-xs font-semibold">{targets.enabled ?? 0}</p>
            </div>
            <div className="rounded-lg bg-foreground/[0.025] px-2 py-1.5">
              <p className="text-[9px] text-foreground/35">Registered</p>
              <p className="text-xs font-semibold">{targets.registered ?? 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {subsystems.map((item) => (
          <Badge key={item.subsystem_key} className="bg-background/65 text-foreground/55">
            {item.display_name}: UNIVERSAL
          </Badge>
        ))}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 text-[10px] text-foreground/35">
        <span>Architecture v{fabric?.architecture?.version ?? "1.2.0"}</span>
        <span>{fabric?.governance_path ?? "M4"} • bypass {fabric?.private_bypass_allowed ? "allowed" : "forbidden"}</span>
      </div>

      {notice && (
        <p className="mt-2 rounded-lg bg-amber-300/[0.08] px-2.5 py-2 text-[10px] text-amber-700 dark:text-amber-100">
          {notice}
        </p>
      )}
    </div>
  );
}
